import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import {
  IconCheck,
  IconDownload,
  IconShare2,
  IconAlertTriangle,
  IconUserQuestion,
  IconX,
  IconChevronLeft,
} from '@tabler/icons-react';
import { capture } from '@tupai/analytics/client';
import { useLinksStore } from '@/store/links-store';
import {
  ApiError,
  getChildSummary,
  regenerateChildSummary,
  getChapters,
  getCreatedAssignments,
} from '@/lib/api';
import type {
  ChildSummaryResponse,
  ChildSummaryRecommendation,
  AssignmentSummary,
} from '@/lib/api';
import GateNoticeModal from '@/components/gate-notice-modal';
import AssignSheet from '@/components/assign-sheet';
import { SummaryReportBody } from '@/components/summary-report-body';
import { generateSummaryPdf, shareSummaryPdf, canSharePdf } from '@/lib/pdf';
import { fmtDayMonth, periodLabel } from '@/lib/format';
import { useLanguage } from '@/lib/language';
import { useTranslation } from '@tupai/i18n/react';
import { apiErrorMessage } from '@/lib/api-errors';
import tupaiPose3 from '@tupai/tokens/assets/spot/tupai-pose-3.svg';

export const Route = createFileRoute('/_protected/summary/$linkId')({
  component: SummaryPage,
});

function childFromLink(link: {
  link_id: string;
  other_party_name?: string | null;
  other_party_phone: string;
  other_party_detail?: string | null;
  student_streak?: number;
  student_nuts?: number;
}) {
  const parts = (link.other_party_detail ?? '').split(' · ');
  const [syllabus, level] = parts.length >= 2 ? parts : [parts[0], undefined];
  return {
    link_id: link.link_id,
    name: link.other_party_name || link.other_party_phone,
    avatar: (link.other_party_name || '?').charAt(0).toUpperCase(),
    level: level || '',
    syllabus: syllabus || '',
    streak: link.student_streak !== undefined ? `Streak: ${link.student_streak} Days` : '-',
    nuts: link.student_nuts !== undefined ? `Nuts: ${link.student_nuts}` : '-',
    accuracy: '-',
  };
}

// `report_viewed` (Phase 2.7, B3): ChildSummaryResponse now carries `id` and
// `read` (`lib/api.ts`), closing the gap Phase 2.3 had to skip this page
// for entirely — see that phase's report for the original discrepancy.
//
// `is_first_view` is NOT simply `!data.read`: a freshly-generated report is
// pre-marked `read: true` server-side (saveReport, `lib/summary.ts`) purely
// so the hub/bell's unread dot doesn't flag a report the parent just watched
// generate — that's a notification-clearing concern, not a "have they seen
// the content before" one. A fresh generation IS honestly the first view
// regardless of that pre-mark, so `from_cache: false` (this call actually
// generated new content) forces `true`; only a cached load falls back to
// `!data.read`.
function SummaryPage() {
  const { linkId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { parentLinks, showToast, parentLinksLoaded } = useLinksStore();
  const locale = useLanguage();
  const { t } = useTranslation('hub');
  const { t: tc } = useTranslation('common');

  // The hub's "Generate new"/"Generate report" arrive with this flag: land
  // straight on the generation screen and regenerate, instead of showing the
  // cached report first. Captured at render so StrictMode's second effect
  // run sees the same value after the history state is cleared below.
  const wantGenerate = location.state.generate === true;

  // `initialSpinner` is the lightweight "still fetching" state (no chrome of
  // its own — a plain spinner) — most loads are a cached read and resolve
  // well under this. `summaryGenerating` is the full mascot screen: it only
  // switches on once a request is still in flight past MASCOT_DELAY_MS,
  // which is what a genuine generation looks like and a fast cached read
  // never does. That's the "fetching cached (fast, no X)" vs. "generating
  // (mascot + X)" split without needing the response to arrive first.
  const [initialSpinner, setInitialSpinner] = useState(!wantGenerate);
  const [summaryGenerating, setSummaryGenerating] = useState(wantGenerate);
  const [summaryStep, setSummaryStep] = useState(0);
  const [childSummaryData, setChildSummaryData] = useState<ChildSummaryResponse | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  const [gateCode, setGateCode] = useState<string | null>(null);
  const [retryTick, setRetryTick] = useState(0);

  // null = not-yet-loaded OR failed — either way AssignSheet treats it as
  // "unknown", so a fetch failure degrades to plain Assign buttons rather
  // than blocking the report.
  const [createdAssignments, setCreatedAssignments] = useState<AssignmentSummary[] | null>(null);
  // The sheet's target chapter; null closes it. Opened immediately on tap —
  // chapter_title_ms fills in later if the lookup below resolves in time.
  const [assignChapter, setAssignChapter] = useState<{
    chapter_id: string;
    chapter_title: string;
    chapter_title_ms?: string;
  } | null>(null);

  const link = linkId
    ? parentLinks.find((l) => l.link_id === linkId && l.status === 'approved')
    : null;

  const fetchedForLinkRef = useRef<string | null>(null);
  // Monotonic id so a slow createdAssignments response can never clobber a
  // fresher one (same defensive pattern as the summary fetch below).
  const assignmentsFetchIdRef = useRef(0);
  // Per-page-load cache of getChapters() results, keyed "syllabus:grade" —
  // avoids refetching the chapter list on every recommendation tap.
  const chaptersCacheRef = useRef<Map<string, { chapter_id: string; title_ms?: string }[]>>(
    new Map()
  );

  const refreshCreatedAssignments = () => {
    const id = ++assignmentsFetchIdRef.current;
    getCreatedAssignments()
      .then((res) => {
        if (id !== assignmentsFetchIdRef.current) return;
        setCreatedAssignments(res.assignments);
      })
      .catch(() => {
        if (id !== assignmentsFetchIdRef.current) return;
        setCreatedAssignments(null);
      });
  };

  useEffect(() => {
    refreshCreatedAssignments();
  }, []);

  const MASCOT_DELAY_MS = 400;
  const MASCOT_MIN_DWELL_MS = 1000;

  // Single-flight guard for the regenerate WRITE. StrictMode re-runs the
  // mount effect (cleanup resets fetchedForLinkRef so the re-run can own the
  // UI state), which is fine for the GET path but issued TWO regenerate
  // POSTs — two stored rows 0.8s apart, seen live once LLM prose made the
  // duplicates differ. Both runs now share one in-flight promise.
  const regenInFlightRef = useRef<{ key: string; promise: Promise<ChildSummaryResponse> } | null>(
    null
  );
  const regenerateOnce = (id: string): Promise<ChildSummaryResponse> => {
    if (regenInFlightRef.current?.key === id) return regenInFlightRef.current.promise;
    const promise = regenerateChildSummary(id).finally(() => {
      if (regenInFlightRef.current?.promise === promise) regenInFlightRef.current = null;
    });
    regenInFlightRef.current = { key: id, promise };
    return promise;
  };

  useEffect(() => {
    if (!linkId) return;
    const fetchKey = `${linkId}:${retryTick}`;
    if (fetchedForLinkRef.current === fetchKey) return;

    fetchedForLinkRef.current = fetchKey;

    let aborted = false;
    let mascotShownAt: number | null = null;
    setSummaryStep(0);
    setChildSummaryData(null);
    setFetchError(null);

    let mascotTimer: ReturnType<typeof setTimeout> | undefined;
    if (wantGenerate) {
      // Deliberate generation (hub's Generate buttons): the mascot screen is
      // the landing state, no spinner-first grace period. Clear the history
      // state so a reload shows the cached report instead of regenerating.
      // Keep TanStack's __TSR_* keys; wiping them breaks its history index.
      window.history.replaceState({ ...window.history.state, generate: undefined }, '');
      mascotShownAt = Date.now();
      setInitialSpinner(false);
      setSummaryGenerating(true);
    } else {
      setInitialSpinner(true);
      setSummaryGenerating(false);
      mascotTimer = setTimeout(() => {
        if (aborted) return;
        mascotShownAt = Date.now();
        setInitialSpinner(false);
        setSummaryGenerating(true);
      }, MASCOT_DELAY_MS);
    }

    const reveal = (fn: () => void) => {
      if (aborted) return;
      if (mascotShownAt !== null) {
        const remaining = MASCOT_MIN_DWELL_MS - (Date.now() - mascotShownAt);
        if (remaining > 0) {
          setTimeout(() => {
            if (!aborted) fn();
          }, remaining);
          return;
        }
      }
      fn();
    };

    const fetchPromise = wantGenerate
      ? regenerateOnce(linkId).catch((err) => {
          // A deliberate generate that can't run falls back to the cached
          // report with an honest toast, rather than dead-ending. The
          // refusal arrives in ~200ms — cancel the mascot's minimum dwell
          // so "Generating…" never plays for work that isn't happening
          // (user decision 2026-08-06: the mascot shows only when
          // generation is genuinely running).
          if (err instanceof ApiError && (err.code === 'cooldown' || err.code === 'no_changes')) {
            mascotShownAt = null;
            showToast('error', err.code === 'cooldown' ? t('toastCooldown') : t('toastNoChanges'));
            return getChildSummary(linkId);
          }
          throw err;
        })
      : getChildSummary(linkId);

    fetchPromise
      .then((data) => {
        if (aborted) return;
        clearTimeout(mascotTimer);
        reveal(() => {
          setChildSummaryData(data);
          setSummaryGenerating(false);
          setInitialSpinner(false);
        });
      })
      .catch((err) => {
        if (aborted) return;
        clearTimeout(mascotTimer);
        reveal(() => {
          setSummaryGenerating(false);
          setInitialSpinner(false);
          // First-ever summary on a lapsed household — generation is gated
          // (previously-generated reports come back 200 and stay viewable).
          if (err instanceof ApiError && err.code === 'feature_blocked') {
            setGateCode('feature_blocked');
          } else {
            setFetchError(apiErrorMessage(err, tc));
          }
        });
      });

    return () => {
      aborted = true;
      clearTimeout(mascotTimer);
      fetchedForLinkRef.current = null;
    };
  }, [linkId, retryTick]);

  useEffect(() => {
    if (!summaryGenerating) return;
    setSummaryStep(0);
    const timer = setTimeout(() => setSummaryStep(1), 1200);
    return () => clearTimeout(timer);
  }, [summaryGenerating]);

  // `report_viewed` — see the module header for is_first_view's reasoning.
  // Skipped (not fabricated) when the server hasn't sent `id` yet (older
  // server, ground rule: never fabricate a required property).
  const reportViewedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!childSummaryData?.id) return;
    if (reportViewedForRef.current === childSummaryData.id) return;
    const isFirstView = childSummaryData.from_cache ? childSummaryData.read === false : true;
    if (childSummaryData.from_cache && typeof childSummaryData.read !== 'boolean') return;
    reportViewedForRef.current = childSummaryData.id;
    const ageDays = Math.max(
      0,
      Math.floor((Date.now() - new Date(childSummaryData.generated_at).getTime()) / 86_400_000)
    );
    capture('report_viewed', {
      report_id: childSummaryData.id,
      is_first_view: isFirstView,
      age_days: ageDays,
    });
  }, [childSummaryData]);

  // Generation gated (no cached report to fall back to) — offer the paywall,
  // and return to the children list on dismiss.
  if (gateCode) {
    return (
      <div className="flex-1 bg-surface">
        <GateNoticeModal code={gateCode} onClose={() => navigate({ to: '/children' })} />
      </div>
    );
  }

  if (summaryGenerating) {
    const childName = link ? childFromLink(link).name : t('summaryFallbackName');
    return (
      <div className="flex-1 flex flex-col bg-surface">
        <div className="px-2 py-2 flex-shrink-0">
          <button
            aria-label={t('cancel')}
            onClick={() => navigate({ to: '/children/$linkId', params: { linkId } })}
            className="w-11 h-11 rounded-pill flex items-center justify-center text-content hover:bg-surface transition-colors cursor-pointer"
          >
            <IconX size={20} stroke={2.5} />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-6 -mt-11">
          <div className="relative w-20 h-20 bg-surface rounded-pill flex items-center justify-center shadow-md animate-pulse">
            <img src={tupaiPose3} alt="" className="size-10 animate-bounce" />
            <div className="absolute inset-0 rounded-pill border-4 border-t-primary border-primary/10 animate-spin" />
          </div>
          <div className="space-y-2 max-w-xs">
            <h2 className="text-base font-extrabold text-content">
              {t('generatingSummary', { childName })}
            </h2>
            <p className="text-sm text-muted font-semibold min-h-8 flex items-center justify-center leading-relaxed">
              {summaryStep === 0 && t('generatingStep0')}
              {summaryStep === 1 && t('generatingStep1')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (initialSpinner) {
    return (
      <div className="flex-1 flex items-center justify-center bg-surface">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-pill animate-spin" />
      </div>
    );
  }

  if (!link) {
    if (!parentLinksLoaded) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-pill animate-spin" />
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-4 bg-surface">
        <div className="w-16 h-16 rounded-pill bg-bg-peach flex items-center justify-center">
          <IconUserQuestion size={28} stroke={2} className="text-primary" />
        </div>
        <div className="space-y-1.5 max-w-xs">
          <h2 className="text-base font-extrabold text-content">{t('childNotFound')}</h2>
          <p className="text-sm text-muted font-semibold leading-relaxed">
            {t('childNotFoundBody')}
          </p>
        </div>
        <button
          onClick={() => navigate({ to: '/children' })}
          className="cta-primary max-w-[12rem] cursor-pointer"
        >
          {t('backToChildren')}
        </button>
      </div>
    );
  }

  if (fetchError || !childSummaryData) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-4 bg-surface">
        <div className="w-16 h-16 rounded-pill bg-bg-peach flex items-center justify-center">
          <IconAlertTriangle size={28} stroke={2} className="text-primary" />
        </div>
        <div className="space-y-1.5 max-w-xs">
          <h2 className="text-base font-extrabold text-content">{t('couldNotLoadReport')}</h2>
          <p className="text-sm text-muted font-semibold leading-relaxed">
            {fetchError ?? tc('somethingWentWrong')}
          </p>
        </div>
        <button
          onClick={() => setRetryTick((t) => t + 1)}
          className="cta-primary max-w-[12rem] cursor-pointer"
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  const child = childFromLink(link);
  const childSyllabus = child.syllabus || 'KSSM';
  const childGrade = child.level || '';

  // Opens the sheet immediately with the chapter's English title, then
  // resolves chapter_title_ms in the background — the lookup must never
  // delay the sheet opening (resolve-then-set into the same state object).
  const handleAssignFromSummary = (rec: ChildSummaryRecommendation) => {
    setAssignChapter({ chapter_id: rec.chapterId, chapter_title: rec.name });

    const cacheKey = `${childSyllabus}:${childGrade}`;
    const cached = chaptersCacheRef.current.get(cacheKey);
    const applyTitleMs = (chapters: { chapter_id: string; title_ms?: string }[]) => {
      chaptersCacheRef.current.set(cacheKey, chapters);
      const match = chapters.find((c) => c.chapter_id === rec.chapterId);
      if (match?.title_ms) {
        setAssignChapter((prev) =>
          prev && prev.chapter_id === rec.chapterId
            ? { ...prev, chapter_title_ms: match.title_ms }
            : prev
        );
      }
    };
    if (cached) {
      applyTitleMs(cached);
    } else {
      getChapters(childSyllabus, childGrade)
        .then(applyTitleMs)
        .catch(() => {
          // Lookup failure: the sheet already opened with chapter_title_ms
          // undefined — nothing more to do.
        });
    }
  };

  const assignedFor = (rec: ChildSummaryRecommendation): AssignmentSummary | null => {
    if (!createdAssignments || !linkId) return null;
    const matches = createdAssignments.filter(
      (a) => a.chapter_id === rec.chapterId && a.link_id === linkId
    );
    if (matches.length === 0) return null;
    // Several matches (re-assigned after completion) — the most recent wins.
    return matches.reduce((latest, a) => (a.assigned_at > latest.assigned_at ? a : latest));
  };

  const pdfParams = {
    childName: child.name,
    syllabus: childSyllabus,
    grade: childGrade,
    generatedAt: childSummaryData.generated_at,
    periodDays: childSummaryData.period_days,
    overview: childSummaryData.overview,
    strengths: childSummaryData.strengths,
    improvements: childSummaryData.improvements,
    recommendations: childSummaryData.recommendations,
    appUsage: childSummaryData.appUsage,
    locale,
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      {/* Prototype page-head: back + left-aligned report headline with the
          date · period sub line. */}
      <div className="bg-surface border-b border-border-light pl-1 pr-4 py-2 flex-shrink-0 safe-area-top flex items-center gap-1">
        <button
          aria-label={t('back')}
          onClick={() => navigate({ to: '/children/$linkId', params: { linkId } })}
          className="w-11 h-11 rounded-pill flex items-center justify-center text-content transition-all hover:bg-bg cursor-pointer shrink-0"
        >
          <IconChevronLeft size={20} stroke={2.5} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-bold text-content truncate">
            {childSummaryData.title ?? t('reportFallbackTitle', { childName: child.name })}
          </p>
          <p className="text-sm text-muted font-semibold truncate">
            {fmtDayMonth(childSummaryData.generated_at, locale)} ·{' '}
            {periodLabel(childSummaryData.period_start, childSummaryData.period_end, locale)}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-5">
        <SummaryReportBody
          data={childSummaryData}
          assignable
          onAssign={handleAssignFromSummary}
          assignedFor={assignedFor}
          onAssignedTap={(a) =>
            a.status === 'completed'
              ? navigate({
                  to: '/children/$linkId/homework/$assignmentId',
                  params: { linkId, assignmentId: a.assignment_id },
                })
              : navigate({ to: '/children/$linkId/homework', params: { linkId } })
          }
        />

        {/* Footer Actions — generation lives on the hub's Generate report CTA
            only (user decision 2026-08-06: in-page Regenerate button removed). */}
        <p className="text-[10px] text-hint font-semibold text-center leading-relaxed px-2">
          {t('refreshNote')}
        </p>
        <div className="flex gap-3 pb-2">
          <button
            onClick={() => {
              generateSummaryPdf(pdfParams);
              setPdfDownloaded(true);
              setTimeout(() => setPdfDownloaded(false), 2000);
            }}
            className="flex-1 h-11 bg-surface border border-border hover:border-primary text-sm font-bold text-content hover:text-primary rounded-md transition-all cursor-pointer active:scale-98 flex items-center justify-center gap-1.5"
          >
            {pdfDownloaded ? (
              <>
                <IconCheck size={14} stroke={2.5} /> {t('downloaded')}
              </>
            ) : (
              <>
                <IconDownload size={14} stroke={2.5} /> {t('downloadPdf')}
              </>
            )}
          </button>
          {canSharePdf && (
            <button
              onClick={() => shareSummaryPdf(pdfParams)}
              className="flex-1 h-11 bg-surface border border-border hover:border-primary text-sm font-bold text-content hover:text-primary rounded-md transition-all cursor-pointer active:scale-98 flex items-center justify-center gap-1.5"
            >
              <IconShare2 size={14} stroke={2.5} /> {t('share')}
            </button>
          )}
        </div>
      </div>

      <AssignSheet
        open={assignChapter !== null}
        onClose={() => setAssignChapter(null)}
        chapter={assignChapter}
        preselectLinkId={linkId}
        existingAssignments={createdAssignments}
        onAssigned={refreshCreatedAssignments}
        onGate={setGateCode}
      />
    </div>
  );
}
